import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Video, CheckCircle2, AlertTriangle, Info, ShieldAlert } from 'lucide-react';
import { API_URL } from '../../../utils/apiUrl';
import { validatePlaybackUrl, validateThumbnailUrl } from '../../../utils/videoUrlValidation';
import CreatomateDiagnosticPanel from './CreatomateDiagnosticPanel';
import VideoTemplateConfigSection from './VideoTemplateConfigSection';

const STATUS_OPTIONS = [
  { value: 'NOT_STARTED', label: 'Başlanmadı', color: '#6B7280' },
  { value: 'SHOOTING_REQUIRED', label: 'Çekim Bekliyor', color: '#B45309' },
  { value: 'SHOOTING_COMPLETED', label: 'Çekim Tamamlandı', color: '#0284C7' },
  { value: 'EDITING', label: 'Kurgu Aşamasında', color: '#7C3AED' },
  { value: 'READY_FOR_REVIEW', label: 'İnceleme Bekliyor', color: '#D97706' },
  { value: 'APPROVED', label: 'Onaylandı', color: '#059669' },
  { value: 'PUBLISHED', label: 'YAYINDA', color: '#065F46' },
  { value: 'NEEDS_REVISION', label: 'Revizyon Gerekli', color: '#DC2626' },
  { value: 'REJECTED', label: 'Reddedildi', color: '#991B1B' },
  { value: 'ARCHIVED', label: 'Arşivlendi', color: '#6B7280' },
];

const PROVIDER_OPTIONS = [
  { value: 'BUNNY_STREAM', label: 'Bunny Stream' },
  { value: 'CDN', label: 'CDN (Genel)' },
  { value: 'MANUAL', label: 'Manuel URL' },
];

const TYPE_OPTIONS = [
  { value: 'STANDARD_TEST_VIDEO', label: 'Standart Test Videosu' },
  { value: 'SHORT_SOCIAL_CUT', label: 'Kısa Sosyal Kesit (yakında)' },
  { value: 'PREMIUM_PRODUCT_VIDEO', label: 'Premium Ürün Videosu (yakında)' },
];

const emptyVideo = {
  provider: 'MANUAL',
  bunnyVideoId: '',
  playbackUrl: '',
  thumbnailUrl: '',
  status: 'NOT_STARTED',
  type: 'STANDARD_TEST_VIDEO',
  aspectRatio: '9:16',
  durationSeconds: '',
  title: '',
  publicCaption: '',
  internalNotes: '',
};

/**
 * VideoManagementSection — Admin sub-form for TestettiK video ops.
 * TT-VIDEO-002: enforces strict whitelist validation client-side (Bunny/CDN only).
 * Backend re-validates the same rules; frontend validation is UX-only.
 */
const VideoManagementSection = ({ productCategory, productId, video, onChange }) => {
  const v = { ...emptyVideo, ...(video || {}) };
  const [template, setTemplate] = useState(null);
  const [bunnyMode, setBunnyMode] = useState(false);
  const [creatomateConfigured, setCreatomateConfigured] = useState(false);
  const [creatomateBusy, setCreatomateBusy] = useState(false);
  const [creatomateStatus, setCreatomateStatus] = useState(v.creatomateStatus || null);
  const [creatomateUrl, setCreatomateUrl] = useState(v.creatomateRenderUrl || null);
  const [creatomateMessage, setCreatomateMessage] = useState('');
  // TT-PRODUCT-IMAGE-GALLERY-001: admin-selectable duration (Otomatik / 30s / 40s)
  const [creatomateDuration, setCreatomateDuration] = useState('auto');
  const [creatomateInfo, setCreatomateInfo] = useState(null); // {durationSeconds, imageCount}
  // TT-P0-CREATOMATE-MP4-FIX + AI-VOICEOVER: track invalid-output + voiceover status
  const [creatomateOutputInvalid, setCreatomateOutputInvalid] = useState(!!v.creatomateOutputInvalid);
  const [creatomateVoiceover, setCreatomateVoiceover] = useState(v.creatomateVoiceoverStatus || null);
  const [creatomateError, setCreatomateError] = useState(v.creatomateErrorMessage || null);
  // TT-CREATOMATE-LOW-RES-MP4-HANDLING-011: low-resolution warning surface (admin-only)
  const [creatomateResolutionWarning, setCreatomateResolutionWarning] = useState(!!v.creatomateResolutionWarning);
  const [creatomateResolutionWarningReason, setCreatomateResolutionWarningReason] = useState(v.creatomateResolutionWarningReason || null);
  const [creatomateDetectedWidth, setCreatomateDetectedWidth] = useState(v.creatomateDetectedWidth || null);
  const [creatomateDetectedHeight, setCreatomateDetectedHeight] = useState(v.creatomateDetectedHeight || null);
  const [creatomateIsLowResolution, setCreatomateIsLowResolution] = useState(!!v.creatomateIsLowResolution);
  // v016: stale render + visual quality surface
  const [currentRenderIsStale, setCurrentRenderIsStale] = useState(false);
  const [visualQualityScore, setVisualQualityScore] = useState(null);
  const [visualQualityGrade, setVisualQualityGrade] = useState(null);
  const [visualQualityWarnings, setVisualQualityWarnings] = useState([]);
  const [renderMode, setRenderMode] = useState(null);
  const [templateSource, setTemplateSource] = useState(null);
  const [finalVideoImageUrl, setFinalVideoImageUrl] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/video-config`)
      .then(r => {
        setBunnyMode(!!r.data?.bunny_configured);
        setCreatomateConfigured(!!r.data?.creatomate_configured);
      })
      .catch(() => { setBunnyMode(false); setCreatomateConfigured(false); });
  }, []);

  useEffect(() => {
    if (!productId) return;
    axios.get(`${API_URL}/api/admin/products/${productId}/creatomate-video-status`)
      .then(r => {
        setCurrentRenderIsStale(!!r.data?.currentRenderIsStale);
        setVisualQualityScore(r.data?.visualQualityScore ?? null);
        setVisualQualityGrade(r.data?.visualQualityGrade ?? null);
        setVisualQualityWarnings(r.data?.visualQualityWarnings || []);
        setRenderMode(r.data?.renderMode ?? null);
        setTemplateSource(r.data?.templateSource ?? null);
        setFinalVideoImageUrl(r.data?.finalVideoImageUrl ?? null);
      })
      .catch(() => { /* silent */ });
  }, [productId]);

  useEffect(() => {
    if (!productCategory) { setTemplate(null); return; }
    axios.get(`${API_URL}/api/video-templates`, { params: { category: productCategory } })
      .then(r => setTemplate(r.data?.supported ? r.data : (r.data?.editing_checklist ? r.data : null)))
      .catch(() => setTemplate(null));
  }, [productCategory]);

  const set = (patch) => onChange({ ...v, ...patch });

  // TT-VIDEO-002: live validation errors (null = OK, string = Turkish error message)
  const playbackError = v.playbackUrl ? validatePlaybackUrl(v.playbackUrl) : null;
  const thumbnailError = v.thumbnailUrl ? validateThumbnailUrl(v.thumbnailUrl) : null;
  // TT-VIDEO-003-ADDON: video duration validation (optional; when provided must be positive number)
  const durationError = (() => {
    const raw = v.durationSeconds;
    if (raw === null || raw === undefined || raw === '') return null;
    const num = Number(raw);
    if (!Number.isFinite(num) || num < 0) return 'Video süresi saniye cinsinden pozitif bir sayı olmalıdır.';
    return null;
  })();
  const publishBlocked = v.status === 'PUBLISHED' && (!v.playbackUrl || !v.thumbnailUrl || !!playbackError || !!thumbnailError);
  const isLivePublicly = v.status === 'PUBLISHED' && !!v.playbackUrl && !playbackError;

  return (
    <div className="border-2 border-neutral-300 rounded-md p-4 md:col-span-2" data-testid="video-management-section">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Video className="w-5 h-5" style={{ color: 'var(--green-main)' }} />
        <h4 className="text-sm font-black uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Video Yönetimi (TestettiK)</h4>
        <span
          className="text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest"
          style={{ background: STATUS_OPTIONS.find(s => s.value === v.status)?.color || '#9CA3AF', color: '#FFF' }}
          data-testid="video-current-status"
        >
          {STATUS_OPTIONS.find(s => s.value === v.status)?.label || v.status}
        </span>
        {isLivePublicly && (
          <span className="text-[9px] font-black px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 uppercase tracking-widest">Halkın Görünümü</span>
        )}
      </div>

      {/* Bunny mode indicator */}
      <div className="text-xs mb-3 flex items-center gap-1.5 p-2 rounded" style={{ background: bunnyMode ? '#F0FDF4' : '#FEF3C7', color: bunnyMode ? '#166534' : '#78350F' }}>
        <Info className="w-3.5 h-3.5 flex-shrink-0" />
        {bunnyMode
          ? 'Bunny Stream yapılandırılmış. API yüklemesi sonraki sürümde kullanılabilir.'
          : 'Bunny Stream yapılandırılmamış. API Key veya gerekli ayarlar eksik. Video ID / Playback URL / Thumbnail URL alanlarını manuel olarak girin.'}
      </div>

      {/* TT-VIDEO-002: whitelist policy notice */}
      <div className="text-[11px] mb-3 flex items-start gap-1.5 p-2 rounded bg-slate-50 border border-slate-200" style={{ color: '#334155' }}>
        <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: '#0F766E' }} />
        <div>
          <p className="font-black uppercase tracking-wider mb-0.5" style={{ fontSize: 10 }}>Kaynak Politikası (TT-VIDEO-002)</p>
          Yalnızca doğrudan Bunny/CDN video bağlantıları kabul edilir (<code className="font-mono">*.b-cdn.net</code>, <code className="font-mono">cdn/video/media.testettik.com</code>). YouTube, Vimeo, TikTok, Instagram, Facebook, iframe oynatıcılar ve Bunny iframe player bağlantıları KESİNLİKLE reddedilir.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-bold mb-1 text-neutral-600">Durum</label>
          <select value={v.status} onChange={e => set({ status: e.target.value })}
            className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="video-status-select">
            {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold mb-1 text-neutral-600">Sağlayıcı</label>
          <select value={v.provider} onChange={e => set({ provider: e.target.value })}
            className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="video-provider-select">
            {PROVIDER_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold mb-1 text-neutral-600">Video Türü</label>
          <select value={v.type} onChange={e => set({ type: e.target.value })}
            className="w-full border border-neutral-300 rounded px-3 py-2 text-sm">
            {TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold mb-1 text-neutral-600">Bunny Video ID</label>
          <input type="text" value={v.bunnyVideoId || ''} onChange={e => set({ bunnyVideoId: e.target.value })}
            placeholder={bunnyMode ? 'Örn: abc123-def4-...' : 'Manuel modda opsiyonel'}
            className="w-full border border-neutral-300 rounded px-3 py-2 text-sm font-mono" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-bold mb-1 text-neutral-600">Playback URL *</label>
          <input type="url" value={v.playbackUrl || ''} onChange={e => set({ playbackUrl: e.target.value })}
            placeholder="https://vz-abc.b-cdn.net/.../play_720p.mp4"
            className={`w-full border rounded px-3 py-2 text-sm font-mono ${playbackError ? 'border-red-500 bg-red-50' : 'border-neutral-300'}`}
            data-testid="video-playback-url"
            aria-invalid={!!playbackError}
            aria-describedby={playbackError ? 'video-playback-error' : undefined}
          />
          {playbackError && (
            <p id="video-playback-error" className="mt-1 text-xs font-semibold text-red-700 flex items-start gap-1" data-testid="video-playback-url-error">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              {playbackError}
            </p>
          )}
          {!playbackError && v.playbackUrl && (
            <p className="mt-1 text-[11px] font-semibold text-emerald-700 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Geçerli Bunny/CDN bağlantısı.
            </p>
          )}
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-bold mb-1 text-neutral-600">Thumbnail URL *</label>
          <input type="url" value={v.thumbnailUrl || ''} onChange={e => set({ thumbnailUrl: e.target.value })}
            placeholder="https://vz-abc.b-cdn.net/.../thumbnail.jpg"
            className={`w-full border rounded px-3 py-2 text-sm font-mono ${thumbnailError ? 'border-red-500 bg-red-50' : 'border-neutral-300'}`}
            data-testid="video-thumbnail-url"
            aria-invalid={!!thumbnailError}
            aria-describedby={thumbnailError ? 'video-thumbnail-error' : undefined}
          />
          {thumbnailError && (
            <p id="video-thumbnail-error" className="mt-1 text-xs font-semibold text-red-700 flex items-start gap-1" data-testid="video-thumbnail-url-error">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              {thumbnailError}
            </p>
          )}
          {!thumbnailError && v.thumbnailUrl && (
            <p className="mt-1 text-[11px] font-semibold text-emerald-700 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Geçerli Bunny/CDN kapak görseli.
            </p>
          )}
        </div>
        <div>
          <label className="block text-xs font-bold mb-1 text-neutral-600">En-Boy Oranı</label>
          <select value={v.aspectRatio || '9:16'} onChange={e => set({ aspectRatio: e.target.value })}
            className="w-full border border-neutral-300 rounded px-3 py-2 text-sm">
            <option value="9:16">9:16 (Dikey — TestettiK standardı)</option>
            <option value="16:9">16:9 (Yatay)</option>
            <option value="1:1">1:1 (Kare)</option>
          </select>
        </div>
        <div data-testid="video-duration-field">
          <label className="block text-xs font-bold mb-1 text-neutral-600" htmlFor="video-duration-input">Video Süresi (saniye)</label>
          <input
            id="video-duration-input"
            type="number"
            min="0"
            step="1"
            inputMode="numeric"
            value={v.durationSeconds ?? ''}
            onChange={e => {
              const raw = e.target.value;
              if (raw === '') { set({ durationSeconds: null }); return; }
              const num = Number(raw);
              set({ durationSeconds: Number.isFinite(num) ? num : raw });
            }}
            className={`w-full border rounded px-3 py-2 text-sm ${durationError ? 'border-red-500 bg-red-50' : 'border-neutral-300'}`}
            placeholder="Örn: 45"
            aria-invalid={!!durationError}
            aria-describedby={durationError ? 'video-duration-error' : 'video-duration-help'}
            data-testid="video-duration-input"
          />
          {durationError ? (
            <p id="video-duration-error" className="mt-1 text-xs font-semibold text-red-700 flex items-start gap-1" data-testid="video-duration-error">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              {durationError}
            </p>
          ) : (
            <p id="video-duration-help" className="mt-1 text-[11px]" style={{ color: 'var(--text-secondary-strong)' }}>
              Final videonun toplam süresi. Örn: 45, 60. Video henüz hazırlanmadıysa boş bırakılabilir.
            </p>
          )}
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-bold mb-1 text-neutral-600">Video Başlığı (dahili)</label>
          <input type="text" value={v.title || ''} onChange={e => set({ title: e.target.value })}
            className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-bold mb-1 text-neutral-600">Halka Açık Alt Yazı</label>
          <input type="text" value={v.publicCaption || ''} onChange={e => set({ publicCaption: e.target.value })}
            placeholder="Bu video, ürünün TestettiK bağımsız test süreci kapsamında hazırlanmıştır."
            className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-bold mb-1 text-neutral-600">Dahili Notlar (halka açık DEĞİL)</label>
          <textarea value={v.internalNotes || ''} onChange={e => set({ internalNotes: e.target.value })} rows="2"
            className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" />
        </div>
      </div>

      {/* Category checklist */}
      {template && (
        <div className="mt-4 pt-4 border-t border-neutral-200 space-y-4">
          {/* 1) Çekim */}
          {template.checklist?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--green-main)' }} />
                <p className="text-xs font-black uppercase tracking-wider">Çekim Kontrol Listesi — {template.category}</p>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500 uppercase tracking-wider">Yönerge</span>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-1.5" data-testid="video-checklist-shooting">
                {template.checklist.map((c, i) => (
                  <li key={i} className="text-xs flex items-start gap-2 py-0.5" style={{ color: 'var(--text-secondary-strong)' }}>
                    <span className="mt-[3px] inline-block w-3.5 h-3.5 rounded-sm border border-neutral-400 flex-shrink-0" aria-hidden="true"></span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
              {template.focus?.length > 0 && (
                <>
                  <p className="text-[10px] font-black uppercase tracking-wider mt-3 mb-1" style={{ color: 'var(--text-muted)' }}>Değerlendirme Odağı</p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{template.focus.join(' · ')}</p>
                </>
              )}
            </div>
          )}

          {/* 2) Kurgu */}
          {template.editing_checklist?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-4 h-4" style={{ color: '#7C3AED' }} />
                <p className="text-xs font-black uppercase tracking-wider">Kurgu Kontrol Listesi</p>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500 uppercase tracking-wider">Yönerge</span>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-1.5" data-testid="video-checklist-editing">
                {template.editing_checklist.map((c, i) => (
                  <li key={i} className="text-xs flex items-start gap-2 py-0.5" style={{ color: 'var(--text-secondary-strong)' }}>
                    <span className="mt-[3px] inline-block w-3.5 h-3.5 rounded-sm border border-neutral-400 flex-shrink-0" aria-hidden="true"></span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 3) Onay */}
          {template.approval_checklist?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-4 h-4" style={{ color: '#059669' }} />
                <p className="text-xs font-black uppercase tracking-wider">Onay Kontrol Listesi</p>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500 uppercase tracking-wider">Yönerge</span>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-1.5" data-testid="video-checklist-approval">
                {template.approval_checklist.map((c, i) => (
                  <li key={i} className="text-xs flex items-start gap-2 py-0.5" style={{ color: 'var(--text-secondary-strong)' }}>
                    <span className="mt-[3px] inline-block w-3.5 h-3.5 rounded-sm border border-neutral-400 flex-shrink-0" aria-hidden="true"></span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[10px] mt-2 italic" style={{ color: 'var(--text-muted)' }}>
                Not: Bu listeler süreç yönergesidir. Kontrol durumu ürün kaydında saklanmaz.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Publish validation warning: only meaningful when trying to publish */}
      {v.status === 'PUBLISHED' && publishBlocked && (
        <div className="mt-3 flex items-start gap-2 p-2 rounded bg-amber-50 border border-amber-300" data-testid="video-publish-blocked-warning">
          <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-900 font-semibold">
            Yayınlamak için geçerli bir Bunny/CDN Playback URL ve Thumbnail URL gereklidir. Backend eksik veya politika dışı bağlantılarla yayınlamayı reddedecektir.
          </p>
        </div>
      )}
      {v.status !== 'PUBLISHED' && (
        <p className="mt-3 text-[11px]" style={{ color: 'var(--text-muted)' }}>
          Bu ürün için video henüz YAYINDA değil — Playback URL ve Thumbnail URL alanları zorunlu değildir. Ürün, video URL&apos;leri boşken de kaydedilebilir.
        </p>
      )}

      {/* TT-CREATOMATE-VIDEO-001: Automatic video generation subsection (admin-only) */}
      <div className="mt-4 pt-4 border-t border-neutral-200" data-testid="creatomate-section">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <ShieldAlert className="w-4 h-4" style={{ color: '#7C3AED' }} />
          <p className="text-xs font-black uppercase tracking-wider">Otomatik Video Üretimi</p>
          <span className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest ${creatomateConfigured ? 'bg-emerald-100 text-emerald-800' : 'bg-neutral-200 text-neutral-600'}`}>
            {creatomateConfigured ? 'Yapılandırılmış' : 'Yapılandırılmamış'}
          </span>
        </div>
        <p className="text-[11px] mb-2" style={{ color: 'var(--text-secondary)' }}>
          {creatomateConfigured
            ? 'Creatomate yapılandırılmış. Otomatik video üretimi kullanılabilir.'
            : 'Creatomate API Key eksik veya placeholder. Otomatik video üretimi kullanılamaz.'}
        </p>
        {/* Duration selector — spec: Otomatik / 30 saniye / 40 saniye */}
        <div className="mb-2 flex items-center gap-2 flex-wrap" data-testid="creatomate-duration-row">
          <label className="text-[11px] font-bold uppercase tracking-wider text-neutral-600">Video Süresi</label>
          <select
            value={creatomateDuration}
            onChange={(e) => setCreatomateDuration(e.target.value)}
            disabled={creatomateBusy}
            className="text-xs border border-neutral-300 rounded px-2 py-1"
            data-testid="creatomate-duration-select"
          >
            <option value="auto">Otomatik (görsel sayısına göre)</option>
            <option value="30">30 saniye</option>
            <option value="40">40 saniye</option>
          </select>
          <span className="text-[10px] text-neutral-500">Otomatik: 1&nbsp;→&nbsp;20s · 2&nbsp;→&nbsp;24s · 3&nbsp;→&nbsp;30s · 4&nbsp;→&nbsp;35s · 5&nbsp;→&nbsp;40s</span>
        </div>
        <button
          type="button"
          disabled={!creatomateConfigured || !productId || creatomateBusy || (creatomateStatus && ['planned','processing','waiting','transcribing','rendering'].includes(creatomateStatus))}
          onClick={async () => {
            if (!productId) return;
            setCreatomateBusy(true); setCreatomateMessage(''); setCreatomateInfo(null);
            setCreatomateOutputInvalid(false); setCreatomateError(null);
            try {
              const r = await axios.post(
                `${API_URL}/api/admin/products/${productId}/generate-creatomate-video`,
                { duration: creatomateDuration },
              );
              setCreatomateStatus(r.data?.creatomateStatus || 'planned');
              setCreatomateInfo({
                durationSeconds: r.data?.durationSeconds,
                imageCount: r.data?.imageCount,
              });
              setCreatomateVoiceover(r.data?.voiceoverStatus || null);
              setCreatomateMessage('Video üretimi başlatıldı.');
            } catch (e) {
              setCreatomateMessage(e?.response?.data?.detail || 'Video üretimi başlatılamadı. Lütfen daha sonra tekrar deneyin.');
            } finally {
              setCreatomateBusy(false);
            }
          }}
          className={`text-xs font-black uppercase tracking-wider px-4 py-2 rounded border-2 transition ${(!creatomateConfigured || creatomateBusy) ? 'border-neutral-300 text-neutral-400 cursor-not-allowed' : 'border-purple-700 text-purple-800 hover:bg-purple-700 hover:text-white'}`}
          data-testid="creatomate-generate-button"
        >
          {creatomateBusy ? 'Başlatılıyor…' : ((creatomateOutputInvalid || creatomateStatus === 'failed') ? 'Yeniden Dene' : 'Otomatik Video Oluştur')}
        </button>
        <button
          type="button"
          disabled={!productId || !creatomateStatus}
          onClick={async () => {
            if (!productId) return;
            try {
              const r = await axios.get(`${API_URL}/api/admin/products/${productId}/creatomate-video-status`);
              setCreatomateStatus(r.data?.creatomateStatus || null);
              setCreatomateUrl(r.data?.creatomateRenderUrl || null);
              setCreatomateOutputInvalid(!!r.data?.creatomateOutputInvalid);
              setCreatomateVoiceover(r.data?.creatomateVoiceoverStatus || null);
              setCreatomateError(r.data?.creatomateErrorMessage || null);
              // TT-CREATOMATE-LOW-RES-MP4-HANDLING-011: refresh low-res warning fields
              setCreatomateResolutionWarning(!!r.data?.creatomateResolutionWarning);
              setCreatomateResolutionWarningReason(r.data?.creatomateResolutionWarningReason || null);
              setCreatomateDetectedWidth(r.data?.creatomateDetectedWidth || null);
              setCreatomateDetectedHeight(r.data?.creatomateDetectedHeight || null);
              setCreatomateIsLowResolution(!!r.data?.creatomateIsLowResolution);
              // v016: stale detection + visual quality surface
              setCurrentRenderIsStale(!!r.data?.currentRenderIsStale);
              setVisualQualityScore(r.data?.visualQualityScore ?? null);
              setVisualQualityGrade(r.data?.visualQualityGrade ?? null);
              setVisualQualityWarnings(r.data?.visualQualityWarnings || []);
              setRenderMode(r.data?.renderMode ?? null);
              setTemplateSource(r.data?.templateSource ?? null);
              setFinalVideoImageUrl(r.data?.finalVideoImageUrl ?? null);
            } catch {
              /* silent */
            }
          }}
          className="ml-2 text-xs font-bold px-3 py-2 rounded border border-neutral-300 hover:border-neutral-500"
          data-testid="creatomate-refresh-button"
        >
          Durumu Yenile
        </button>
        {creatomateStatus && (
          <div className="mt-2 text-[11px]" data-testid="creatomate-status-display">
            <span className="font-black uppercase tracking-wider mr-1">Durum:</span>
            <span>{creatomateStatus}</span>
            {creatomateInfo?.durationSeconds && (
              <span className="ml-2 text-neutral-500" data-testid="creatomate-info-line">
                • {creatomateInfo.durationSeconds} sn · {creatomateInfo.imageCount ?? '?'} görsel
              </span>
            )}
            {/* Voiceover status pill */}
            {creatomateVoiceover === 'included' && (
              <span className="ml-2 inline-block px-2 py-0.5 rounded bg-purple-100 border border-purple-300 text-purple-800 font-bold text-[10px]" data-testid="creatomate-voiceover-included">
                AI seslendirme dahil
              </span>
            )}
            {creatomateVoiceover === 'blocked_no_provider' && (
              <span className="ml-2 inline-block px-2 py-0.5 rounded bg-amber-100 border border-amber-300 text-amber-900 font-bold text-[10px]" data-testid="creatomate-voiceover-blocked">
                AI seslendirme için sağlayıcı yapılandırması gerekiyor
              </span>
            )}

            {/* TT-CREATOMATE-FAILED-STATUS-PAYLOAD-RCA-FIX-007: distinct banner for failed status */}
            {creatomateStatus === 'failed' && (
              <div className="mt-2 p-2 rounded bg-red-50 border border-red-300" data-testid="creatomate-failed-banner">
                {/* TT-CREATOMATE-FIX-INVALID-ELEMENT-TYPE-010: element-validation-specific copy */}
                {creatomateError && /RenderScript eleman tipi/i.test(creatomateError) ? (
                  <>
                    <p className="text-red-900 font-bold">Video üretimi başarısız oldu. RenderScript eleman tipi geçersiz.</p>
                    <p className="text-red-800 mt-1">{creatomateError}</p>
                  </>
                ) : (
                  <>
                    <p className="text-red-900 font-bold">Video üretimi başarısız oldu. Creatomate render işlemi tamamlanamadı.</p>
                    {creatomateError && <p className="text-red-800 mt-1">Hata detayı: {creatomateError}</p>}
                    {!creatomateError && (
                      <p className="text-red-700 mt-1">Creatomate hata detayı döndürmedi. Backend loglarında güvenli tanı bilgisi oluşturuldu.</p>
                    )}
                  </>
                )}
                <p className="text-red-700 mt-1">&quot;Yeniden Dene&quot; butonuyla tekrar deneyebilirsiniz.</p>
              </div>
            )}
            {/* TT-CREATOMATE-MP4-BLACK-OUTPUT-RCA-FIX-005: invalid output banner (JPG or wrong aspect ratio) */}
            {creatomateOutputInvalid && creatomateStatus !== 'failed' && (
              <div className="mt-2 p-2 rounded bg-red-50 border border-red-300" data-testid="creatomate-invalid-output">
                <p className="text-red-900 font-bold">
                  {creatomateError && /en-boy/i.test(creatomateError)
                    ? 'Video üretimi başarısız: MP4 en-boy oranı geçersiz (dikey 9:16 bekleniyordu).'
                    : 'Video üretimi başarısız: Creatomate MP4 yerine görsel çıktı üretti.'}
                </p>
                {creatomateError && <p className="text-red-800 mt-1">{creatomateError}</p>}
                <p className="text-red-700 mt-1">&quot;Yeniden Dene&quot; butonuyla tekrar deneyebilirsiniz.</p>
              </div>
            )}

            {creatomateUrl && creatomateStatus === 'succeeded' && !creatomateOutputInvalid && (
              <div className="mt-1 p-2 rounded bg-emerald-50 border border-emerald-200" data-testid="creatomate-success-result">
                <p className="text-emerald-900 font-semibold">
                  {creatomateVoiceover === 'included'
                    ? 'Video hazır — AI seslendirme dahil, henüz public yayında değil.'
                    : 'Video hazır — henüz public yayında değil.'}
                </p>
                <a href={creatomateUrl} target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline break-all">{creatomateUrl}</a>
                {/* TT-CREATOMATE-LOW-RES-MP4-HANDLING-011: low-resolution warning (admin-only) */}
                {creatomateResolutionWarning && (
                  <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-300" data-testid="creatomate-low-res-warning">
                    <p className="text-amber-900 font-bold">
                      Video düşük çözünürlükte üretildi. Final kalite için Creatomate planı veya render kalite ayarı kontrol edilmeli.
                    </p>
                    {creatomateDetectedWidth && creatomateDetectedHeight && (
                      <p className="text-amber-800 mt-1" data-testid="creatomate-detected-resolution">
                        Üretilen çözünürlük: {creatomateDetectedWidth}x{creatomateDetectedHeight}
                        {creatomateIsLowResolution ? ' (düşük çözünürlük)' : ''}
                      </p>
                    )}
                    {creatomateResolutionWarningReason === 'dimensions_unavailable_from_provider' && (
                      <p className="text-amber-800 mt-1">
                        Çözünürlük bilgisi Creatomate tarafından döndürülmedi — MP4 formatı doğrulandı, görsel doğrulama admin bağlantısı üzerinden yapılmalı.
                      </p>
                    )}
                    <p className="text-amber-700 mt-1 text-[11px]">
                      Bu video otomatik olarak yayınlanmaz. Public yayına almadan önce görsel kaliteyi manuel olarak kontrol edin.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {creatomateMessage && (
          <p className="mt-2 text-xs font-semibold" style={{ color: '#B45309' }} data-testid="creatomate-message">{creatomateMessage}</p>
        )}

        {/* v016: Stale render warning — admin changed template/media settings after last render */}
        {currentRenderIsStale && (
          <div className="mt-3 p-2 rounded bg-amber-50 border-2 border-amber-400 flex items-start gap-2" data-testid="creatomate-stale-render-warning">
            <AlertTriangle className="w-4 h-4 text-amber-800 flex-shrink-0 mt-0.5" />
            <p className="text-xs font-bold text-amber-900">
              Şablon ayarları değişti. Mevcut MP4 eski ayarlarla üretilmiş olabilir. Yeni render alın.
            </p>
          </div>
        )}

        {/* v016: Visual quality score + grade + template source */}
        {(visualQualityScore !== null || renderMode || templateSource) && (
          <div className="mt-3 p-2 rounded bg-slate-50 border border-slate-300" data-testid="creatomate-quality-panel">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-700">Video Kalite Kontrolü</p>
              {visualQualityScore !== null && (
                <span
                  className={`text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest ${
                    visualQualityGrade === 'premium' ? 'bg-emerald-600 text-white' :
                    visualQualityGrade === 'good' ? 'bg-emerald-500 text-white' :
                    visualQualityGrade === 'acceptable' ? 'bg-amber-500 text-white' :
                    visualQualityGrade === 'weak' ? 'bg-orange-600 text-white' :
                    'bg-red-700 text-white'
                  }`}
                  data-testid="creatomate-quality-score-badge"
                >
                  Skor: {visualQualityScore}/100 · {visualQualityGrade}
                </span>
              )}
            </div>
            {templateSource && (
              <p className="text-[11px] text-slate-700" data-testid="creatomate-template-source">
                <span className="font-bold">Render kaynağı:</span> <span className="font-mono">{templateSource}</span>
                {templateSource === 'internal_renderscript' && (
                  <span className="ml-2 text-amber-800 font-semibold">
                    (Bu şablon internal RenderScript fallback ile üretiliyor; Creatomate template_id bağlı değil.)
                  </span>
                )}
              </p>
            )}
            {finalVideoImageUrl && (
              <p className="text-[11px] text-slate-700 break-all">
                <span className="font-bold">Render&apos;a giden görsel URL:</span> <span className="font-mono">{finalVideoImageUrl}</span>
              </p>
            )}
            {visualQualityScore !== null && visualQualityScore < 60 && (
              <div className="mt-2 p-2 rounded bg-red-50 border border-red-300 flex items-start gap-2" data-testid="creatomate-quality-fail-banner">
                <AlertTriangle className="w-3.5 h-3.5 text-red-700 flex-shrink-0 mt-0.5" />
                <p className="text-xs font-bold text-red-900">
                  Bu video görsel kalite kontrolünden geçmedi. Public yayına alınmamalı.
                </p>
              </div>
            )}
            {(visualQualityWarnings || []).length > 0 && (
              <ul className="mt-1 space-y-0.5" data-testid="creatomate-quality-warnings">
                {visualQualityWarnings.map((w, i) => (
                  <li key={i} className="text-[11px] text-amber-900 flex items-start gap-1">
                    <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5 text-amber-700" /> {w}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* TT-CREATOMATE-V014-CATEGORY-TEMPLATE-PRESET-SYSTEM: admin-only template config */}
        <VideoTemplateConfigSection productId={productId} />

        {/* TT-CREATOMATE-DIAGNOSTIC-UI-009: admin-only A/B diagnostic runner */}
        <CreatomateDiagnosticPanel productId={productId} />
      </div>

      {v.aspectRatio && v.aspectRatio !== '9:16' && (
        <div className="mt-2 flex items-start gap-2 p-2 rounded bg-blue-50 border border-blue-200">
          <Info className="w-4 h-4 text-blue-700 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-900 font-medium">
            Uyarı: TestettiK standart formatı 9:16&apos;dır. Farklı bir en-boy oranı seçtiniz, oynatıcı yine çalışır ancak stil dikey kart görünümünden farklı olacaktır.
          </p>
        </div>
      )}
    </div>
  );
};

export default VideoManagementSection;
